import React, { useState, useEffect, useCallback } from 'react';
import { agencyPerformanceAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { formatDistanceToNow } from 'date-fns';
import {
  Shield, Search, Loader2, ChevronLeft, ChevronRight
} from 'lucide-react';

export function AgencyAuditLog() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 20 };
      if (search) params.search = search;
      const res = await agencyPerformanceAPI.auditLogs(params);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to load audit logs:', err);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const totalPages = Math.ceil(total / 20);

  return (
    <Card data-testid="agency-audit-log">
      <CardHeader className="flex flex-row items-center justify-between py-3">
        <CardTitle className="text-base font-heading flex items-center gap-2">
          <Shield size={16} className="text-sky-500" />
          Activity Log
          {total > 0 && <Badge variant="outline" className="text-[10px]">{total} events</Badge>}
        </CardTitle>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-7 h-8 w-40 text-xs"
              data-testid="audit-search-input"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <ScrollArea className="h-[350px]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <Shield size={28} className="mb-2" />
              <p className="text-sm">No audit logs found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {logs.map((log, i) => (
                <div key={log.audit_id || i} className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700" data-testid={`audit-entry-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px]">{log.action?.replace(/_/g, ' ')}</Badge>
                      <Badge variant="outline" className="text-[10px] text-slate-500">{log.entity_type}</Badge>
                    </div>
                    <span className="text-[10px] text-slate-400">
                      {log.created_at ? formatDistanceToNow(new Date(log.created_at), { addSuffix: true }) : ''}
                    </span>
                  </div>
                  {log.details && <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 line-clamp-2">{log.details}</p>}
                  <p className="text-[10px] text-slate-400 mt-1">by {log.performed_by || 'System'}</p>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
            <span className="text-[10px] text-slate-400">Page {page} of {totalPages}</span>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                <ChevronLeft size={14} />
              </Button>
              <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                <ChevronRight size={14} />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
